import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import axios from "axios";
import { 
  Home, MessageCircle, Video, BookOpen, Users, Wind, Shield,
  Menu, X, LogOut, Settings, User, ChevronDown, Calendar,
  Award, CreditCard, Clock, Heart, Bell, DollarSign, Car,
  Briefcase, Star, Handshake, BarChart3, GraduationCap, Smile, HeartHandshake, Hash, MapPin, FileText, Sparkles, Vote
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import FloatingChatWidget from "./FloatingChatWidget";
import EditableText from "./EditableText";
import PreviewModeToggle from "./PreviewModeToggle";
import WhatsNewBanner from "./WhatsNewBanner";
import PageBlocks from "./PageBlocks";
import { useBrand } from "../providers/BootstrapProvider";

// Persistent trial / verification banner shown above main content
const TrialBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const adminNoBanner = user.is_admin && !user.must_change_password;
  if (adminNoBanner) return null;

  // Forced password change: admin-issued temp password, or an old password below the policy.
  if (user.must_change_password) {
    return (
      <div className="px-4 py-3 bg-[#FEF2F2] border-b border-[#FCA5A5]" data-testid="must-change-pw-banner">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-start gap-2.5">
            <Shield className="w-4 h-4 mt-0.5 shrink-0 text-[#DC2626]" />
            <p className="text-sm text-[#6B4E8A]">
              {user.must_change_password_reason === "policy"
                ? <>Our password rules have been strengthened. <strong>Please choose a new password</strong> (10+ characters with uppercase, lowercase, a number and a special character) to keep using DHDH.</>
                : <>An administrator issued a temporary password for your account. <strong>Please change it now</strong> — leaving it in place reduces the security of your account.</>}
            </p>
          </div>
          <Button
            onClick={() => navigate("/profile?tab=password")}
            size="sm"
            className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full shrink-0"
            data-testid="change-pw-now-btn"
          >
            Change password
          </Button>
        </div>
      </div>
    );
  }

  // Verification not yet verified — show prominent banner
  if (user.verification_status !== "verified") {
    const isPending = user.verification_status === "submitted";
    return (
      <div className={`px-4 py-3 ${isPending ? "bg-[#FFF7ED] border-b border-[#FDBA74]" : "bg-[#FEF2F2] border-b border-[#FCA5A5]"}`} data-testid="verification-banner">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-start gap-2.5">
            <Shield className={`w-4 h-4 mt-0.5 shrink-0 ${isPending ? "text-[#D97706]" : "text-[#DC2626]"}`} />
            <p className="text-sm text-[#6B4E8A]">
              {isPending ? (
                <>Your verification is <strong>under review</strong> — we'll unlock the full app within 1-2 business days.</>
              ) : (
                <>Verify your registration to unlock posts, messaging, mentorship, and every other feature. <span className="text-[#64748B]">Photo ID + practice permit required.</span></>
              )}
            </p>
          </div>
          {!isPending && (
            <Button onClick={() => navigate("/verification")} size="sm" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full shrink-0" data-testid="verify-now-btn">
              Verify now
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Trial countdown
  if (user.membership_status === "trialing" && typeof user.trial_days_remaining === "number") {
    const days = user.trial_days_remaining;
    const urgent = days <= 7;
    return (
      <div className={`px-4 py-2.5 ${urgent ? "bg-[#FEF2F2] border-b border-[#FCA5A5]" : "bg-[#FFFBEB] border-b border-[#FDE68A]"}`} data-testid="trial-banner">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-start gap-2.5">
            <Clock className={`w-4 h-4 mt-0.5 shrink-0 ${urgent ? "text-[#DC2626]" : "text-[#D97706]"}`} />
            <p className="text-sm text-[#6B4E8A]">
              {days === 0 ? <>Your trial ends <strong>today</strong> — pick a membership to keep your access.</>
                : <><strong>{days}</strong> day{days !== 1 ? "s" : ""} left in your free trial. Pick a plan to keep going.</>}
            </p>
          </div>
          <Button onClick={() => navigate("/membership")} size="sm" className={urgent ? "bg-[#DC2626] text-white hover:bg-[#B91C1C] rounded-full shrink-0" : "bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full shrink-0"} data-testid="upgrade-now-btn">
            Upgrade now
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

// Screenshot prevention removed — replaced by invisible watermarking on
// sensitive pages (see <InvisibleWatermark />). Watermarks are silent and
// less intrusive than detection-based blackouts.

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const brand = useBrand();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminNotifCount, setAdminNotifCount] = useState(0);

  // Fetch admin notification count
  useEffect(() => {
    if (user?.is_admin) {
      const fetchNotifs = async () => {
        try {
          const res = await axios.get(`${API}/admin/notifications?unread_only=true`);
          setAdminNotifCount(res.data.length);
        } catch {}
      };
      fetchNotifs();
      const interval = setInterval(fetchNotifs, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to logout");
    }
  };

  const navItems = [
    { icon: Home, label: "Dashboard", path: "/dashboard" },
    { icon: Smile, label: "Community", path: "/community" },
    { icon: MessageCircle, label: "Discussions", path: "/discussions" },
    { icon: Users, label: "Messages", path: "/messages" },
    { icon: Hash, label: "Groups", path: "/groups" },
    { icon: Video, label: "Video Calls", path: "/video" },
    { icon: Calendar, label: "Events", path: "/events" },
    { icon: MapPin, label: "Meetups", path: "/meetups" },
    { icon: Award, label: "CE Tracker", path: "/ce-tracker" },
    { icon: FileText, label: "Notes", path: "/notes" },
    { icon: Clock, label: "Shift Tracker", path: "/shifts" },
    { icon: Heart, label: "Health", path: "/health" },
    { icon: DollarSign, label: "Finances", path: "/finance" },
    { icon: Car, label: "Mileage", path: "/mileage" },
    { icon: BarChart3, label: "Wages", path: "/wages" },
    { icon: Vote, label: "Polls", path: "/polls" },
    { icon: Briefcase, label: "Jobs", path: "/jobs" },
    { icon: Star, label: "Clinic Ratings", path: "/clinic-ratings" },
    { icon: Handshake, label: "Partners", path: "/partners" },
    { icon: GraduationCap, label: "New Grads", path: "/new-grads" },
    { icon: HeartHandshake, label: "Mentorship", path: "/mentorship" },
    { icon: BookOpen, label: "Resources", path: "/resources" },
    { icon: Users, label: "Members", path: "/members" },
    { icon: Wind, label: "Breathing", path: "/breathing" },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* Mobile Header */}
      <header className="lg:hidden backdrop-blur-xl bg-white/80 border-b border-[#E5DFEC]/50 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-[#F0ECF7] rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-[#6B4E8A]" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={brand.logo_url} alt="DHDH" className="w-9 h-9 rounded-full object-cover" />
            <span className="font-semibold text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>DHDH</span>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-8 h-8 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center">
                {user?.name?.[0] || 'U'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/verification")}>
                <Shield className="w-4 h-4 mr-2" />
                Verification
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/membership")}>
                <CreditCard className="w-4 h-4 mr-2" />
                Membership
              </DropdownMenuItem>
              {user?.is_admin && (
                <>
                  {(user?.admin_role === "founder" || user?.role === "founder" || user?.is_admin) && (
                    <DropdownMenuItem onClick={() => navigate("/founder")} data-testid="nav-founder-dashboard">
                      <Sparkles className="w-4 h-4 mr-2 text-[#8DB5A0]" />
                      Founder Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-[#EF4444]" data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E5DFEC] z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#E5DFEC]">
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center gap-3">
                <img src={brand.logo_url} alt="DHDH" className="w-11 h-11 rounded-full object-cover" />
                <span className="font-semibold text-sm text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Development Hub</span>
              </Link>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1 hover:bg-[#F0ECF7] rounded"
              >
                <X className="w-5 h-5 text-[#64748B]" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const slug = item.path.replace(/^\//, "").replace(/\//g, "-") || "home";
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                    ${isActive(item.path)
                      ? 'bg-[#6B4E8A] text-white'
                      : 'text-[#64748B] hover:bg-[#F0ECF7] hover:text-[#6B4E8A]'}
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <EditableText
                    blockKey={`nav.${slug}`}
                    fallback={item.label}
                    as="span"
                    className="font-medium"
                  />
                </Link>
              );
            })}
            {/* Founder Dashboard link — prominent, above Admin, founder/admin only */}
            {(user?.admin_role === "founder" || user?.role === "founder" || user?.is_admin) && (
              <Link
                to="/founder"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-colors mt-2 border-t border-[#E5DFEC] pt-4
                  ${isActive('/founder')
                    ? 'bg-gradient-to-r from-[#8DB5A0] to-[#8DB5A0]/80 text-white shadow-sm'
                    : 'text-[#8DB5A0] hover:bg-[#8DB5A0]/10 font-semibold'}
                `}
                data-testid="nav-founder"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">Founder</span>
              </Link>
            )}
            {/* Admin notification link */}
            {user?.is_admin && (
              <Link
                to="/admin"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                  ${isActive('/admin')
                    ? 'bg-[#6B4E8A] text-white'
                    : 'text-[#64748B] hover:bg-[#F0ECF7] hover:text-[#6B4E8A]'}
                `}
                data-testid="nav-admin"
              >
                <Shield className="w-5 h-5" />
                <span className="font-medium">Admin</span>
                {adminNotifCount > 0 && (
                  <span className="ml-auto px-2 py-0.5 bg-[#EF4444] text-white text-xs rounded-full" data-testid="admin-notif-badge">
                    {adminNotifCount}
                  </span>
                )}
              </Link>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-[#E5DFEC]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F0ECF7] transition-colors" data-testid="user-dropdown-btn">
                  <div className="w-10 h-10 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center font-medium">
                    {user?.name?.[0] || 'U'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-[#6B4E8A] line-clamp-1">{user?.name}</p>
                    <p className="text-xs text-[#64748B] line-clamp-1">{user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#64748B]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/verification")}>
                  <Shield className="w-4 h-4 mr-2" />
                  Verification
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-[#EF4444]" data-testid="sidebar-logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64">
        <TrialBanner />
        <WhatsNewBanner />
        {children}
        {/* Global platform disclaimer (shown on every authenticated page load) */}
        <div className="lg:ml-0 px-6 md:px-8 lg:px-12 pb-8 pt-4" data-testid="layout-platform-disclaimer">
          <p className="text-[11px] text-[#94A3B8] leading-relaxed text-center max-w-3xl mx-auto border-t border-[#E5DFEC] pt-4">
            DHDH is a peer community platform. Content shared by members does not constitute clinical, legal, or financial advice. Always consult a licensed professional.
          </p>
        </div>
      </main>

      {/* Site-wide Floating Live Chat */}
      <FloatingChatWidget />
      <PreviewModeToggle />
      {/* Founder-managed "global" text blocks — appear above the watermark on every authenticated page */}
      <PageBlocks pageKey="global" placement="top" className="max-w-5xl mx-auto px-4 pt-3" />
      {/* Phase 72: InvisibleWatermark mounted globally at AuthenticatedLayout
          level so every route (public + protected + legal + admin + future)
          inherits it. No per-page configuration required. */}
    </div>
  );
};

export default Layout;
